import { useCallback, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import IcBaselineCheck from '~icons/ic/baseline-check'
import IcBaselineClose from '~icons/ic/baseline-close'
import {
  useMembershipCardActions,
  type SubscriptionInfo,
} from 'src/features/Membership/components/MembershipPlanCard/useMembershipCardActions'
import { useMembershipCardState } from 'src/features/Membership/components/MembershipPlanCard/useMembershipCardState'
import {
  type GetAllPaymentItemsQuery,
  Interval,
} from 'src/services/apollo/generated/graphql'
import { useMyMembership } from 'src/services/apollo/membership'
import { cn } from 'src/utils/cx'
import { PLAN_DISPLAY } from '../constants'
import {
  type LandingPlanDisplay,
  useLandingPricing,
} from '../hooks/useLandingPricing'
import { useNavigateToGenerator } from '../hooks/useNavigateToGenerator'
import css from '../Landing.module.css'
import { useFadeUp } from './useFadeUp'

/** Paid plan tier ids rendered by the pricing grid. */
const PAID_TIERS: Array<1 | 2 | 3> = [1, 2, 3]
/** Paid tiers all advertise four features in the i18n schema. */
const PAID_PLAN_FEATURE_COUNT = 4

export default function PricingSection({
  initialPaymentItems,
}: {
  /**
   * Payment items pre-fetched on the server by `fetchPaymentItemsForSSR`.
   * When provided, `useLandingPricing` can emit real prices on the first
   * SSR render instead of rendering `PaidPlanCardFallback`.
   */
  initialPaymentItems?: GetAllPaymentItemsQuery['paymentItems']
} = {}) {
  const { t } = useTranslation('blackboard', { keyPrefix: 'welcome' })
  const [isYearly, setIsYearly] = useState(true)
  const interval = isYearly ? Interval.Year : Interval.Month
  const { plans } = useLandingPricing(interval, { initialPaymentItems })
  const fadeRef = useFadeUp()
  const navigateToGenerator = useNavigateToGenerator()

  // Get free plan features from translations
  const freePlanFeatures = [
    t('pricing.plans.0.features.0'),
    t('pricing.plans.0.features.1'),
    t('pricing.plans.0.features.2'),
  ]

  return (
    <section
      id="pricing"
      className="py-24"
      style={{ background: 'var(--landing-pink-bg2)' }}
    >
      <div className="mx-auto max-w-[1200px] px-6">
        <div ref={fadeRef} className={cn(css.fadeUp, 'text-center')}>
          <div className="mb-2 text-sm font-bold uppercase tracking-wider text-[--landing-pink]">
            {t('pricing.section-label')}
          </div>
          <h2 className="mb-4 text-4xl font-semibold leading-tight text-gray-900">
            <Trans
              i18nKey="blackboard:welcome.pricing.heading"
              components={{
                highlight: <span className={css.gradientText} />,
              }}
            />
          </h2>
        </div>

        {/* Toggle */}
        <div className="mb-12 mt-8 flex items-center justify-center gap-3.5">
          <span
            className={cn(
              'text-sm font-medium',
              !isYearly ? 'font-bold text-gray-900' : 'text-gray-500',
            )}
          >
            {t('pricing.monthly-plan')}
          </span>
          <button
            onClick={() => setIsYearly(v => !v)}
            className={cn(
              'relative h-[26px] w-12 rounded-full transition-colors',
              isYearly ? 'bg-[--landing-pink]' : 'bg-gray-200',
            )}
            aria-label={t('pricing.toggle-label')}
          >
            <span
              className={cn(
                'absolute left-[3px] top-[3px] h-5 w-5 rounded-full bg-white shadow-sm transition-transform',
                isYearly && 'translate-x-[22px]',
              )}
            />
          </button>
          <span
            className={cn(
              'text-sm font-medium',
              isYearly ? 'font-bold text-gray-900' : 'text-gray-500',
            )}
          >
            {t('pricing.yearly-plan')}
          </span>
          <span
            className={cn(
              'rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-600 transition-opacity',
              isYearly ? 'opacity-100' : 'opacity-0',
            )}
          >
            {t('pricing.save-badge')}
          </span>
        </div>

        {/* Pricing grid */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {/* Free tier card */}
          <div className="flex flex-col rounded-3xl border-[1.5px] border-gray-200 bg-white p-8 text-center transition-transform hover:-translate-y-1">
            <h3 className="mb-1 text-sm font-bold uppercase tracking-wider text-gray-500">
              Free
            </h3>
            <div className="my-4 text-5xl font-black text-gray-900">¥0</div>
            <div className="text-sm text-gray-400">
              {t('pricing.free-forever')}
            </div>
            <ul className="my-5 flex grow flex-col gap-2 text-left">
              {freePlanFeatures.map(f => (
                <li
                  key={f}
                  className="flex items-center gap-2 text-sm text-gray-600"
                >
                  <IcBaselineCheck className="size-4 shrink-0 text-green-500" />
                  {f}
                </li>
              ))}
              <li className="flex items-center gap-2 text-sm text-gray-600">
                <IcBaselineClose className="size-4 shrink-0 text-gray-300" />
                {t('pricing.no-video-generation')}
              </li>
            </ul>
            <button
              onClick={() => navigateToGenerator()}
              className="mt-auto w-full rounded-full border-[1.5px] border-gray-200 bg-white px-6 py-3 text-sm font-semibold text-gray-700 transition-all hover:border-[--landing-pink] hover:bg-[--landing-pink-bg] hover:text-[--landing-pink]"
            >
              {t('pricing.start-free')}
            </button>
          </div>

          {/* Paid tiers
           *
           * Always render three paid plan slots so the SSR HTML ships the
           * full pricing comparison (heading, plan names, feature lists,
           * CTA) — crawlers see all tiers even before Apollo resolves the
           * real price data. On first paint (and any time `useLandingPricing`
           * has not yet produced an available plan for a given tier) we
           * render `PaidPlanCardFallback`, which uses the exact same DOM
           * structure as `PaidPlanCard` but replaces the price block with
           * a placeholder and the subscribe button with a static "Start
           * for free" link. Once Apollo resolves and the plan becomes
           * available the slot swaps to the live interactive card without
           * shifting layout because the two components share a layout
           * footprint.
           */}
          {PAID_TIERS.map(tier => {
            const real = plans.find(p => p.tier === tier)
            if (real?.available && real.plan?.id) {
              return <PaidPlanCard key={tier} plan={real} interval={interval} />
            }
            return <PaidPlanCardFallback key={tier} tier={tier} />
          })}
        </div>
      </div>
    </section>
  )
}

function PaidPlanCard({
  plan,
  interval,
}: {
  plan: LandingPlanDisplay
  interval: Interval
}) {
  const { t } = useTranslation('blackboard', { keyPrefix: 'welcome' })
  const navigateToGenerator = useNavigateToGenerator()
  const { data: { membership, subscription } = {} } = useMyMembership()

  const cardState = useMembershipCardState({
    planTier: plan.tier,
    planId: plan.plan.id,
    interval,
    membership,
    subscription,
  })

  const { executeAction } = useMembershipCardActions()

  // Get features from translations
  const featureCount =
    plan.tier === 1 || plan.tier === 2 || plan.tier === 3 ? 4 : 3
  const features = Array.from({ length: featureCount }, (_, i) =>
    t(`pricing.plans.${plan.tier}.features.${i}`),
  )

  // On a landing page, if the action would be disabled just go to /generator
  const handleClick = useCallback(async () => {
    if (cardState.isDisabled) {
      navigateToGenerator()
      return
    }

    const prices = plan.plan.prices?.[interval]
    if (!prices) {
      navigateToGenerator()
      return
    }

    const priceRecord = Object.fromEntries(
      Object.entries(prices).map(([cur, p]) => [
        cur,
        interval === Interval.Year ? p.perYear : p.perMonth,
      ]),
    ) as SubscriptionInfo['prices']

    await executeAction(cardState.stateAction, cardState.state, {
      planId: plan.plan.id,
      tier: plan.tier,
      interval,
      prices: priceRecord,
      oneTimeMembership: plan.plan.oneTimeMembership,
      isActive: cardState.stateInfo?.hasActiveSubscription ?? false,
    })
  }, [plan, interval, cardState, executeAction, navigateToGenerator])

  return (
    <div
      className={cn(
        'relative flex flex-col rounded-3xl border-[1.5px] p-8 text-center transition-transform hover:-translate-y-1',
        plan.featured
          ? 'border-[--landing-pink] bg-white shadow-[0_8px_32px_rgba(255,131,184,0.12)]'
          : 'border-gray-200 bg-white',
      )}
    >
      {plan.featured && (
        <span
          className={cn(
            css.popularBadge,
            'absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[--landing-pink] px-4 py-1 text-xs font-bold text-white',
          )}
        >
          {t('pricing.popular-badge')}
        </span>
      )}
      <h3 className="mb-1 text-sm font-bold uppercase tracking-wider text-gray-500">
        {plan.name}
      </h3>
      <div className="my-4 text-5xl font-black text-gray-900">
        {plan.perMonth}
      </div>
      <div className="text-sm text-gray-400">{t('pricing.per-month')}</div>
      {plan.perMonthOriginal && (
        <div className="text-sm text-gray-300 line-through">
          {plan.perMonthOriginal}
          {t('pricing.per-month')}
        </div>
      )}
      <ul className="my-5 flex grow flex-col gap-2 text-left">
        {features.map(f => (
          <li key={f} className="flex items-center gap-2 text-sm text-gray-600">
            <IcBaselineCheck className="size-4 shrink-0 text-green-500" />
            {f}
          </li>
        ))}
      </ul>

      {cardState.isDisabled && (
        <p className="mb-2 text-xs text-gray-400">
          {t('pricing.membership-active')}
        </p>
      )}
      <button
        onClick={handleClick}
        className="mt-auto w-full rounded-full bg-[--landing-pink] px-6 py-3 text-sm font-bold text-white shadow-[0_4px_16px_rgba(255,131,184,0.3)] transition-all hover:-translate-y-0.5 hover:bg-[--landing-pink-dark] hover:shadow-[0_8px_24px_rgba(255,131,184,0.4)]"
      >
        {cardState.isDisabled ? t('pricing.go-to-generator') : cardState.label}
      </button>
    </div>
  )
}

/**
 * SSR-safe paid plan card used when Apollo has not yet resolved the
 * per-tier `MembershipPlan` (typically during the first SSR flush and the
 * few milliseconds before the client query settles).
 *
 * Intentionally mirrors the exact DOM shape of `PaidPlanCard`:
 *   - same outer classes (featured vs regular card)
 *   - same popular badge slot
 *   - same h3 + price row + per-month label + feature ul
 *   - same full-width CTA button
 *
 * The price number is replaced by a neutral skeleton block and the
 * subscribe button becomes a static `<a>` pointing at the generator, so
 * crawlers and no-JS visitors always see a complete, clickable pricing
 * comparison. When `useLandingPricing` later produces an `available` plan
 * for this tier, `PricingSection` swaps this card out for the live one;
 * because the DOM structure matches there is no layout shift.
 */
function PaidPlanCardFallback({ tier }: { tier: 1 | 2 | 3 }) {
  const { t } = useTranslation('blackboard', { keyPrefix: 'welcome' })
  const display = PLAN_DISPLAY[tier]
  const featured = display?.featured ?? false

  const features = Array.from({ length: PAID_PLAN_FEATURE_COUNT }, (_, i) =>
    t(`pricing.plans.${tier}.features.${i}`),
  )

  return (
    <div
      className={cn(
        'relative flex flex-col rounded-3xl border-[1.5px] p-8 text-center transition-transform hover:-translate-y-1',
        featured
          ? 'border-[--landing-pink] bg-white shadow-[0_8px_32px_rgba(255,131,184,0.12)]'
          : 'border-gray-200 bg-white',
      )}
    >
      {featured && (
        <span
          className={cn(
            css.popularBadge,
            'absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[--landing-pink] px-4 py-1 text-xs font-bold text-white',
          )}
        >
          {t('pricing.popular-badge')}
        </span>
      )}
      <h3 className="mb-1 text-sm font-bold uppercase tracking-wider text-gray-500">
        {display?.name ?? ''}
      </h3>
      <div className="my-4 flex h-12 items-center justify-center">
        <span
          aria-hidden
          className="h-8 w-24 rounded-md bg-gray-100 dark:bg-white/5"
        />
      </div>
      <div className="text-sm text-gray-400">{t('pricing.per-month')}</div>
      <ul className="my-5 flex grow flex-col gap-2 text-left">
        {features.map(f => (
          <li key={f} className="flex items-center gap-2 text-sm text-gray-600">
            <IcBaselineCheck className="size-4 shrink-0 text-green-500" />
            {f}
          </li>
        ))}
      </ul>
      <a
        href="/generator/image"
        className="mt-auto w-full rounded-full bg-[--landing-pink] px-6 py-3 text-sm font-bold text-white no-underline shadow-[0_4px_16px_rgba(255,131,184,0.3)] transition-all hover:-translate-y-0.5 hover:bg-[--landing-pink-dark] hover:shadow-[0_8px_24px_rgba(255,131,184,0.4)]"
      >
        {t('pricing.start-free')}
      </a>
    </div>
  )
}
